import type { BrandingAssetRef, BrandingPatch, VoiceStageSetting } from "@/api/admin/branding"

/** The branding asset the "image" ground reads. */
export const VOICE_STAGE_ASSET_KEY = "voice_stage_background"

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

type Translate = (key: string, options: { defaultValue: string }) => string

/** A stored setting (null means the sky) as the form's value. */
export function normalizeVoiceStage(
  value: VoiceStageSetting | null | undefined
): VoiceStageSetting {
  return value ?? { mode: "sky" }
}

/** The form's value as the patch field: the sky is stored as "unset". */
export function voiceStagePatch(value: VoiceStageSetting): VoiceStageSetting | null {
  if (value.mode === "sky") return null
  if (value.mode === "color") return { mode: "color", color: value.color.trim() }
  return value
}

export function isValidStageColor(value: string): boolean {
  return HEX_COLOR_RE.test(value.trim())
}

/**
 * One string per value the server would store. The API returns the object
 * with its keys sorted and the hex lower-cased, so comparing JSON of the two
 * objects calls a re-picked stored value a change.
 */
function storedForm(value: VoiceStageSetting | null | undefined): string {
  if (value?.mode === "color") return `color:${value.color.trim().toLowerCase()}`
  if (value?.mode === "image") return `image:${value.ink}`
  return "sky"
}

export function sameVoiceStage(
  a: VoiceStageSetting | null | undefined,
  b: VoiceStageSetting | null | undefined
): boolean {
  return storedForm(a) === storedForm(b)
}

/**
 * Why this value cannot be saved yet, as a message for the admin, or null.
 * The API refuses both; saying so before the request keeps the rest of the
 * form's edits instead of failing the whole save.
 */
export function voiceStageSaveError(
  value: VoiceStageSetting,
  image: BrandingAssetRef | null,
  t: Translate
): string | null {
  if (value.mode === "image" && !image) {
    return t("adminDashboard.branding.voiceStage.imageUploadFirst", {
      defaultValue: "Upload an image before saving the image background.",
    })
  }
  if (value.mode === "color" && !isValidStageColor(value.color)) {
    return t("adminDashboard.branding.voiceStage.colorInvalid", {
      defaultValue: "Use a hex colour like #1f6feb.",
    })
  }
  return null
}

/**
 * The voice window's part of a save. Unchanged, it sends nothing: an image
 * ground whose upload was since removed is still stored, and sending it back
 * would block saving every other branding field until the admin changed a
 * setting they never touched.
 */
export function planVoiceStageSave({
  stored,
  value,
  image,
  t,
}: {
  stored: VoiceStageSetting | null | undefined
  value: VoiceStageSetting
  image: BrandingAssetRef | null
  t: Translate
}): { error: string } | { patch: Pick<BrandingPatch, "voice_stage"> } {
  if (sameVoiceStage(stored, value)) return { patch: {} }
  const error = voiceStageSaveError(value, image, t)
  return error ? { error } : { patch: { voice_stage: voiceStagePatch(value) } }
}
